import { csvExportService } from "./csv-export.service";
import { fileDownloadService } from "./file-download.service";
import { pdfExportService } from "./pdf-export.service";
import { xlsxExportService } from "./xlsx-export.service";
import type {
  CsvExportOptions,
  ExportFormat,
  ExportRequest,
  ExportTableModel,
  PdfExportOptions,
  XlsxExportOptions,
} from "./export.types";

const DEFAULT_FILE_NAME = "export";

const EXPORT_EXTENSIONS: Record<ExportFormat, string> = {
  csv: ".csv",
  pdf: ".pdf",
  excel: ".xlsx",
};

export class ExportService {
  private readonly _handlers = {
    csv: this._handleCsvExport.bind(this),
    pdf: this._handlePdfExport.bind(this),
    excel: this._handleXlsxExport.bind(this),
  } as const;

  async export(request: ExportRequest): Promise<void> {
    await this._handlers[request.format](request);
  }

  private async _handleCsvExport(request: ExportRequest): Promise<void> {
    this._exportCsv(request.table, {
      fileName: this._resolveFileName(request.fileName, request.format),
      separator: request.separator,
      lineBreak: request.lineBreak,
      includeHeaders: request.includeHeaders,
      includeBom: request.includeBom,
      includeExcelSeparatorHint: request.includeExcelSeparatorHint,
    });
  }

  private async _handlePdfExport(request: ExportRequest): Promise<void> {
    const options: PdfExportOptions = {
      fileName: this._resolveFileName(request.fileName, request.format),
      reportTitle: request.reportTitle,
      applicationTitle: request.applicationTitle,
      generatedAt: request.generatedAt,
      pageOrientation: request.pageOrientation,
      pageSize: request.pageSize,
      maxGridWidth: request.maxGridWidth,
    };

    await pdfExportService.download(request.table, options);
  }

  private async _handleXlsxExport(request: ExportRequest): Promise<void> {
    this._exportXlsx(request.table, {
      fileName: this._resolveFileName(request.fileName, request.format),
      sheetName: request.sheetName,
    });
  }

  private _exportCsv(table: ExportTableModel, options: CsvExportOptions = {}): void {
    const csvFile = csvExportService.serialize(table, options);

    fileDownloadService.downloadText(csvFile.fileName, csvFile.content, {
      includeBom: csvFile.includeBom,
      mimeType: csvFile.mimeType,
    });
  }

  private _exportXlsx(table: ExportTableModel, options: XlsxExportOptions = {}): void {
    const xlsxFile = xlsxExportService.serialize(table, options);

    fileDownloadService.downloadBlob(xlsxFile.fileName, xlsxExportService.createBlob(xlsxFile));
  }

  private _resolveFileName(fileName: string | undefined, format: ExportFormat): string {
    const extension = EXPORT_EXTENSIONS[format];
    const normalizedFileName = fileName?.trim() || DEFAULT_FILE_NAME;

    if (normalizedFileName.toLowerCase().endsWith(extension)) {
      return normalizedFileName;
    }

    return `${normalizedFileName}${extension}`;
  }
}

export const exportService = new ExportService();
